// Mapeo de las vistas de Pulso (antes `ViewId` en estado local de src/App.tsx) a rutas reales de
// React Router, y un elemento de ruta por vista. "survey" conserva "/" — es la URL que ya usan
// bookmarks y las Teams tabs configuradas hoy, así que no se puede mover.
//
// Ver docs/superpowers/specs/2026-08-04-menu-unificado-design.md, Fase 1.

import type { ReactElement } from 'react';
import { useParams } from 'react-router-dom';
import type { ViewId } from '@/hooks/useVisibleNavLinks';
import { ErrorBoundary } from '@/components/ErrorBoundary';
import { SurveyView } from '@/views/SurveyView';
import { SafeView } from '@/views/SafeView';
import { FundamentalsView } from '@/views/FundamentalsView';
import { AdminView } from '@/views/AdminView';
import { ExecutiveView } from '@/views/ExecutiveView';

export const VIEW_PATHS: Record<ViewId, string> = {
  survey: '/',
  safe: '/crecimiento',
  fundamentals: '/fundamentales',
  admin: '/admin',
  executive: '/ejecutivo',
};

export const PATH_TO_VIEW: Record<string, ViewId> = Object.fromEntries(
  (Object.entries(VIEW_PATHS) as Array<[ViewId, string]>).map(([view, path]) => [path, view]),
);

/**
 * Resuelve la vista activa (Pulso) para un `pathname` dado. `PATH_TO_VIEW` solo conoce coincidencia
 * EXACTA (mapeo 1:1 de `VIEW_PATHS`), así que rutas hermanas con segmento opcional —como
 * `/admin/:section`, agregada en Task 11 para los deep-links de Fundamentales— no aparecen ahí.
 * Antes de esta función, `PulsoLayout` caía siempre al fallback `'survey'` en esas rutas, lo que
 * rebotaba a un usuario sin módulo Pulso fuera de un deep-link válido a `/admin/...` (hallazgo I-2
 * de la revisión final de "menú unificado").
 *
 * Match exacto primero (rápido, cubre el caso común); si no hay coincidencia exacta, cae a un match
 * por prefijo sobre `VIEW_PATHS`. `'/'` (la vista `survey`) es caso especial: como es prefijo de
 * CUALQUIER pathname, solo participa en el match exacto, nunca en el de prefijo.
 */
export function resolveView(pathname: string): ViewId {
  const exactMatch = PATH_TO_VIEW[pathname];
  if (exactMatch) return exactMatch;

  const prefixMatch = Object.entries(VIEW_PATHS).find(
    ([, path]) => path !== '/' && pathname.startsWith(path),
  );
  return (prefixMatch?.[0] as ViewId | undefined) ?? 'survey';
}

/**
 * Home de la zona admin de Reconocimiento — no es una vista de Pulso, así que no vive en
 * `VIEW_PATHS`, pero se exporta junto a `isAdminPath` porque ambas se comparten entre las dos zonas
 * de admin que gatea el mismo componente Sidebar (ver `isAdminPath`).
 */
export const RECOGNITION_ADMIN_HOME = '/app/admin';

/**
 * Predicado "estoy en zona admin", compartido por las dos zonas de la app que gatean UI a partir de
 * él (Sidebar.tsx: apertura automática del flyout + destino del botón "Administración" en escritorio
 * y en el nav inferior móvil). Pulso usa `/admin*`; Reconocimiento usa `/app/admin*` — dos rutas de
 * admin distintas que comparten el mismo componente Sidebar (hallazgo I-3 de la revisión final de
 * "menú unificado": el predicado vivía duplicado a mano como `pathname.startsWith(VIEW_PATHS.admin)`,
 * que nunca hacía match en la zona `/app/admin`).
 */
export function isAdminPath(pathname: string): boolean {
  return pathname.startsWith(VIEW_PATHS.admin) || pathname.startsWith(RECOGNITION_ADMIN_HOME);
}

/**
 * Ruta vieja de "Administradores" (vista lateral `users` hasta 2026-10-29) y su destino actual: la
 * sección `administradores` del panel. Se conserva el redirect para marcadores y enlaces guardados.
 */
export const LEGACY_USERS_PATH = '/usuarios';
export const ADMINISTRADORES_PATH = `${VIEW_PATHS.admin}/administradores`;

export interface PulsoViewRoute {
  view: ViewId;
  path: string;
  element: ReactElement;
}

/**
 * Ruta `admin/:section`: resuelve el segmento opcional de deep-link (Fundamentales, Fase 3) desde
 * `useParams` y se lo pasa a `AdminView` tal cual — la prop `initialSection` no cambió.
 */
function AdminRoute() {
  const { section } = useParams<{ section?: string }>();
  return <ErrorBoundary><AdminView initialSection={section} /></ErrorBoundary>;
}

/** Un elemento de ruta por ViewId, cada uno envuelto en su propio ErrorBoundary — mismo
 *  aislamiento que hoy da `ViewPanel` en App.tsx. `admin` gana una ruta hermana con un segmento
 *  opcional `:section` para los deep-links de Fundamentales (ver Fase 3, Task 11): dos entradas
 *  comparten `view: 'admin'` a propósito — `AppRouter.tsx` crea un `<Route>` por cada elemento del
 *  arreglo sin asumir `view` único, y ambas rutas resuelven al mismo componente. */
export const PULSO_VIEW_ROUTES: PulsoViewRoute[] = [
  { view: 'survey', path: VIEW_PATHS.survey, element: <ErrorBoundary><SurveyView /></ErrorBoundary> },
  { view: 'safe', path: VIEW_PATHS.safe, element: <ErrorBoundary><SafeView /></ErrorBoundary> },
  { view: 'fundamentals', path: VIEW_PATHS.fundamentals, element: <ErrorBoundary><FundamentalsView /></ErrorBoundary> },
  { view: 'admin', path: VIEW_PATHS.admin, element: <AdminRoute /> },
  { view: 'admin', path: `${VIEW_PATHS.admin}/:section`, element: <AdminRoute /> },
  { view: 'executive', path: VIEW_PATHS.executive, element: <ErrorBoundary><ExecutiveView /></ErrorBoundary> },
];
